import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getClientActivityStats } from '../../services/order.service';

const AnimatedActivityChart = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('This Week');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);
  const dropdownRef = useRef(null);

  const periods = ['This Week', 'This Month', 'This Quarter', 'This Year'];
  
  const { data: statsData, isLoading } = useQuery({
    queryKey: ['clientActivityStats'],
    queryFn: getClientActivityStats
  });

  const currentData = statsData?.stats?.[selectedPeriod] || [];
  const maxValue = currentData.length > 0 ? Math.max(...currentData.map(d => Math.max(d.value, 10)), 10) * 1.2 : 10;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Animation effect
  useEffect(() => {
    setAnimationProgress(0);
    setHoveredPoint(null);
    const timer = setTimeout(() => {
      const animate = () => {
        setAnimationProgress(prev => {
          if (prev >= 1) return 1;
          return prev + 0.015;
        });
      };
      const animationFrame = setInterval(animate, 16);
      setTimeout(() => clearInterval(animationFrame), 1000);
    }, 150);
    
    return () => clearTimeout(timer);
  }, [selectedPeriod]);

  // Create smooth curve path
  const createPath = (points, progress = 1) => {
    if (points.length < 2) return '';
    
    const visiblePoints = Math.ceil(points.length * progress);
    const animatedPoints = points.slice(0, visiblePoints).map((point, index) => ({
      ...point,
      y: point.y
    }));

    if (animatedPoints.length < 2) return '';

    let path = `M ${animatedPoints[0].x} ${animatedPoints[0].y}`;
    
    for (let i = 1; i < animatedPoints.length; i++) {
      const prev = animatedPoints[i - 1];
      const curr = animatedPoints[i];
      
      const cpx = prev.x + (curr.x - prev.x) * 0.5;
      const cpy1 = prev.y;
      const cpy2 = curr.y;
      
      path += ` C ${cpx} ${cpy1}, ${cpx} ${cpy2}, ${curr.x} ${curr.y}`;
    }
    
    return path;
  };

  const chartWidth = 360;
  const chartHeight = 140;
  const padding = 50;
  
  const points = currentData.map((item, index) => ({
    x: padding + (index * (chartWidth - 2 * padding)) / (currentData.length - 1),
    y: chartHeight - padding - ((item.value / maxValue) * (chartHeight - 2 * padding)),
    ...item
  }));

  const handleMouseMove = (event) => {
    if (svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      setMousePosition({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      });
    }
  };

  const handlePointHover = (index, event) => {
    setHoveredPoint(index);
    handleMouseMove(event);
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-lg mx-auto bg-[#F5F5F7] dark:bg-[#1d2026] rounded-[10px] p-8 h-[300px] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#174568] dark:border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto bg-[#F5F5F7] dark:bg-[#1d2026] rounded-[10px] p-8 border border-transparent dark:border-[#32353c]">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-lg md:text-xl md:font-medium md:font-semibold text-[#141522] dark:text-white tracking-tight">Activity</h3>
        
        {/* Custom Select */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 bg-inherit  px-2 py-1.5 text-[12px] font-normal md:font-medium text-[#141522] dark:text-gray-300 focus:outline-none  transition-all duration-200 "
          >
            {selectedPeriod}
            <ChevronDown className={`w-4 h-4 text-slate-500 dark:text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {isDropdownOpen && (
            <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-[#191c22] border border-slate-200 dark:border-[#32353c] rounded-xl shadow-lg py-2 z-10 animate-in fade-in slide-in-from-top-2 duration-200">
              {periods.map(period => (
                <button
                  key={period}
                  onClick={() => {
                    setSelectedPeriod(period);
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full text-left px-2 py-1.5 text-[12px] font-normal transition-colors duration-150 ${
                    selectedPeriod === period 
                      ? 'bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-400 border-r-2 border-blue-500' 
                      : 'text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white dark:bg-[#191c22] rounded-[10px] py-2 border border-transparent dark:border-[#32353c]">
      <div className="relative">
        <svg 
          ref={svgRef}
          width={chartWidth} 
          height={chartHeight} 
          className="overflow-visible"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredPoint(null)}
        >
          {/* Grid lines */}
        {[...Array(currentData.length)].map((_, index) => (
 <line
   key={index}
   x1={padding + (index * (chartWidth - 2 * padding)) / (currentData.length - 1)}
   y1={padding - 20}
   x2={padding + (index * (chartWidth - 2 * padding)) / (currentData.length - 1)}
   y2={chartHeight - padding + 20}
   stroke="#e2e8f0"
   strokeWidth="1"
   opacity={0.6}
 />
))}
          
        
          
          {animationProgress > 0.3 && (
            <path
              d={`${createPath(points, animationProgress)} L ${points[Math.min(Math.floor(points.length * animationProgress), points.length - 1)].x} ${chartHeight - padding} L ${points[0].x} ${chartHeight - padding} Z`}
              fill="url(#areaGradient)"
              opacity={Math.min(animationProgress * 2, 1)}
            />
          )}
          
          {/* Chart line */}
          <path
            d={createPath(points, animationProgress)}
            fill="none"
            className="stroke-[#141522] dark:stroke-blue-400"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              filter: 'drop-shadow(0 4px 6px rgba(59, 130, 246, 0.15))',
            }}
          />
          
          {/* Invisible hover areas */}
          {points.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="15"
              fill="transparent"
              className="cursor-crosshair"
              onMouseEnter={(e) => handlePointHover(index, e)}
              style={{
                opacity: animationProgress > 0.8 ? 1 : 0,
                pointerEvents: animationProgress > 0.8 ? 'all' : 'none'
              }}
            />
          ))}
          
          {/* Visible point only on hover */}
          {hoveredPoint !== null && (
            <g>
              <circle
                cx={points[hoveredPoint].x}
                cy={points[hoveredPoint].y}
                r="5"
                fill="white"
                stroke="#546FFF"
                strokeWidth="4"
                className="animate-in zoom-in duration-150"
                style={{
                  filter: 'drop-shadow(0 4px 8px rgba(59, 130, 246, 0.25))',
                }}
              />
             
            </g>
          )}
          
          {/* X-axis labels */}
          {points.map((point, index) => (
            <text
              key={index}
              x={point.x}
              y={chartHeight - 15}
              textAnchor="middle"
              fontSize="13"
              fill="#64748b"
              fontWeight="600"
              style={{
                opacity: animationProgress > 0.7 ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}
            >
              {point.day}
            </text>
          ))}
          
          {/* Y-axis labels */}
          {[1, 2, 3].map(tick => (
            <text
              key={tick}
              x={30}
              y={chartHeight - padding - (tick * (chartHeight - 1 * padding)) / 3 + 5}
              textAnchor="middle"
              fontSize="12"
              fill="#94a3b8"
              fontWeight="500"
              style={{
                opacity: animationProgress > 0.5 ? 0.8 : 0,
                transition: 'opacity 0.3s ease'
              }}
            >
              {tick}
            </text>
          ))}
        </svg>
        
        {/* Single Tooltip */}
        {hoveredPoint !== null && (
          <div 
            className="absolute pointer-events-none z-20 animate-in fade-in slide-in-from-bottom-2 duration-200"
            style={{
              left: Math.min(Math.max(mousePosition.x - 100, 10), chartWidth - 200),
              top: mousePosition.y - 80,
            }}
          >
            <div className="bg-[#174568] text-white px-4 py-3 rounded-xl shadow-2xl border border-slate-700/50 backdrop-blur-sm">
            
              <div className="text-xs text-slate-100">
                {currentData[hoveredPoint].details}
              </div>
              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-[#174568] rotate-45"></div>
            </div>
          </div>
        )}
      </div>
      </div>
   
    </div>
  );
};

export default AnimatedActivityChart;